import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { HelpCircle, Mic, MicOff, MoveRight, Send } from "lucide-react";
import { ICON_STROKE } from "../components/icons/icon-tokens";
import { ChatTalkButton } from "./voice/ChatTalkButton";
import { getUser, isAllInOneUser } from "../helpers/utils";
import { AssistantMessage } from "./components/AssistantMessage";
import { CustomReasoningWidget } from "./components/CustomReasoningWidget";
import { DisclaimerBanner, useDisclaimerVisible } from "./components/DisclaimerBanner";
import { SupportTicketModal } from "./components/SupportTicketModal";
import { ConversationSidebar } from "./components/ConversationSidebar";
import { ProductionHeader } from "./components/ProductionHeader";
import {
  ScrollToBottomButton,
  useScrollDetection,
} from "./components/ScrollToBottomButton";
import { UserMessage } from "./components/UserMessage";
import { useAutoResizeTextarea } from "./hooks/useAutoResizeTextarea";
import { useKeyboardInset } from "./hooks/useKeyboardInset";
import { useChatMessages } from "./hooks/useChatMessages";
import { useSpeechRecognition } from "./hooks/useSpeechRecognition";
import { mergeToolMessages } from "./utils/mergeToolMessages";
import { useAnalytics, usePricingConfigAgentChat } from "../helpers/hooks";
import { useApp } from "../helpers/app-context";
import type { WidgetStatesMap } from "./types/widgetState";
import type { MessagePart } from "./utils/messageHelpers";
import {
  getNotchMaskStyle,
  NARROW_NOTCH_PATH,
  NARROW_NOTCH_VIEW_W,
  NARROW_NOTCH_VIEW_H,
} from "../components/notchMask";

// Chat notch render size (px). The mask and the stroke-border SVG below
// BOTH use this so they line up exactly. ~29:51 aspect (Sidebar notch).
const CHAT_NOTCH_W = 35;
const CHAT_NOTCH_H = 62;

interface ServerMessagePart {
  type: string;
  text?: string;
  /** Modern format: arbitrary widget payload (set when type starts with "data-"). */
  data?: unknown;
  // Legacy "widget"-typed parts (kept for backwards-compat with old conversations):
  widget?: Record<string, unknown>;
  widgetId?: string;
  status?: string;
}

interface ServerMessage {
  id: string;
  role: string;
  content: string;
  parts?: ServerMessagePart[];
}

/**
 * ErrorDisplay Component
 *
 * Displays user-friendly error messages, NEVER shows raw JSON or validation details.
 * Acts as final safety layer to sanitize any error messages before showing to users.
 */
interface ErrorDisplayProps {
  error: Error;
  onRetry?: () => void;
}

function ErrorDisplay({ error, onRetry }: ErrorDisplayProps) {
  /**
   * Extract user-friendly message from error.
   * This is a safety net - even if error.message contains JSON, we don't show it.
   */
  const getUserFriendlyMessage = (): string => {
    const message = error.message || '';

    // Handle empty or whitespace-only messages
    if (!message || message.trim() === '') {
      return "Something went wrong. Please try again.";
    }

    // Detect if message contains JSON (validation errors leak through)
    // More robust detection: check if JSON-like content is at start/end of message
    const isLikelyJsonError = (): boolean => {
      const trimmed = message.trim();

      // Check for JSON at start/end of message
      if (trimmed.startsWith('[{') || trimmed.startsWith('{')) {
        return true;
      }

      // Check for validation error patterns
      if (message.includes('Type validation') || message.includes('AI_TypeValidationError')) {
        return true;
      }

      // Check for common Zod error patterns
      if (message.match(/"code":\s*"invalid_/i) || message.match(/"expected":\s*"/i)) {
        return true;
      }

      return false;
    };

    if (isLikelyJsonError()) {
      // NEVER show JSON - always fallback to connectivity message
      return "We're having trouble connecting to our AI service. Please try again.";
    }

    // Detect stack traces (multi-line errors with "at " patterns)
    if (message.includes('\n') && message.match(/\s+at\s+/)) {
      console.warn('[ErrorDisplay] Detected stack trace in error message');
      return "Something went wrong. Please try again.";
    }

    // Detect network-related keywords
    if (message.toLowerCase().includes('network') ||
        message.toLowerCase().includes('fetch') ||
        message.toLowerCase().includes('connection')) {
      return "Connection issue. Please check your internet and try again.";
    }

    // If message is short and clean (no special chars), use it
    if (message.length < 100 && !message.includes('[') && !message.includes('{')) {
      return message;
    }

    // For longer messages, truncate if they look safe
    if (message.length <= 200 && !message.includes('[') && !message.includes('{') && !message.includes('\n')) {
      return message.length > 100 ? `${message.substring(0, 97)}...` : message;
    }

    // Generic fallback
    return "Something went wrong. Please try again.";
  };

  const friendlyMessage = getUserFriendlyMessage();

  return (
    <div
      className="w-full max-w-md text-center p-3 rounded-lg"
      style={{
        background: "var(--d-neg-soft)",
        border: "1px solid var(--d-neg-soft)",
      }}
      role="alert"
      aria-live="assertive"
    >
      <p className="text-body text-[var(--d-neg-text)] mb-2">
        {friendlyMessage}
      </p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="text-callout font-medium text-[var(--d-neg-text)] hover:text-[var(--d-neg-text)] hover:underline transition-colors"
          aria-label="Retry sending message"
        >
          Try Again
        </button>
      )}
    </div>
  );
}

type ChatMode = "chat" | "agent";

interface ChatV2ProductionProps {
  /** If true, render desktop sidebar layout (350px fixed width) */
  isDesktop?: boolean;
  /** Callback to close the chat widget */
  onClose?: () => void;
  /** If true, chat is expanded (desktop only) */
  expanded?: boolean;
  /** Toggle expanded state (desktop only) */
  toggleExpanded?: () => void;
}

/**
 * Chat V2 Production - Polished AI chatbot for end users
 *
 * Combines V1 styling (Autumn branding, polished UI) with V2 functionality:
 * - Autumn logo and personalized greeting
 * - V1 color scheme and layout
 * - AI SDK v6 streaming and tool approval
 * - Markdown rendering
 * - Inline widgets (hotel metrics)
 *
 * @see src/chatv2/README.md for architecture details
 */
export function ChatV2Production({
  isDesktop = true,
  onClose,
  expanded = false,
  toggleExpanded,
}: ChatV2ProductionProps) {
  const { trackEvent } = useAnalytics();
  // Tracks whether the disclaimer notice is still showing — used to hide
  // the surrounding hairline dividers in compact mode (when the user
  // dismisses the notice the dividers were leaving a doubled-up gap).
  const disclaimerVisible = useDisclaimerVisible();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [input, setInput] = useState("");
  // Height covered by the on-screen keyboard (0 on desktop / no keyboard) — the
  // mobile panel subtracts it so the composer stays above the keyboard.
  const keyboardInset = useKeyboardInset();
  // ── Composer aura state ──
  // `isComposerFocused` → `.is-active` (slow flow + brighter ring).
  // `isComposerTyping`  → `.is-typing` (fast flow + breathing pulse).
  // Typing flag is cleared 900ms after the last keystroke, matching the
  // Claude Design AI Agent reference. See `.a-composer-aura.*` in
  // `dashboard-tokens.css`.
  const [isComposerFocused, setIsComposerFocused] = useState(false);
  const [isComposerTyping, setIsComposerTyping] = useState(false);
  const composerTypingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleComposerType = useCallback(() => {
    setIsComposerTyping(true);
    if (composerTypingTimer.current) clearTimeout(composerTypingTimer.current);
    composerTypingTimer.current = setTimeout(() => setIsComposerTyping(false), 900);
  }, []);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastFailedMessage, setLastFailedMessage] = useState<string>("");
  const [currentConversationId, setCurrentConversationId] = useState<string>(
    () => crypto.randomUUID() // Generate a stable ID for new chats
  );
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);
  const [isExistingConversation, setIsExistingConversation] = useState(false); // Track if we're viewing an existing conversation
  const [pendingMessages, setPendingMessages] = useState<Record<string, unknown>[] | null>(null); // Store messages to apply after hook re-initializes
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [supportInitialMessage, setSupportInitialMessage] = useState("");
  const { supportPrefill, setSupportPrefill, tourChatModeRequest, setTourChatModeRequest } = useApp() as {
    supportPrefill: string | null;
    setSupportPrefill: (value: string | null) => void;
    tourChatModeRequest: ChatMode | null;
    setTourChatModeRequest: (mode: ChatMode | null) => void;
  };

  const openSupportModal = useCallback((message = "") => {
    setSupportInitialMessage(message);
    setIsSupportModalOpen(true);
  }, []);

  // A prefill set from outside the chat (e.g. the Google API outage callout)
  // opens the Contact Support modal with the draft already filled in, then
  // clears so reopening the chat later starts blank.
  useEffect(() => {
    if (!supportPrefill) return;
    openSupportModal(supportPrefill);
    setSupportPrefill(null);
  }, [supportPrefill, setSupportPrefill, openSupportModal]);

  const [chatMode, setChatMode] = useState<ChatMode>("chat");
  const [currentAgentChatId, setCurrentAgentChatId] = useState<string>(
    () => `chat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  );
  const [isExistingAgentConversation, setIsExistingAgentConversation] = useState(false);
  const [pendingAgentMessages, setPendingAgentMessages] = useState<Record<string, unknown>[] | null>(null);
  const [widgetStates, setWidgetStates] = useState<WidgetStatesMap>({});

  const activeWidgetsRef = useRef<Map<string, { conversationId: string; messageId: string; actionType: string; widgetId?: string; partIndex?: number; useNewPersistence?: boolean }>>(new Map());

  const dismissActiveWidgets = useCallback(async () => {
    const widgets = Array.from(activeWidgetsRef.current.entries());
    activeWidgetsRef.current.clear();
    if (widgets.length === 0) return;
    const { api } = await import("../helpers/api");
    for (const [, info] of widgets) {
      // Support tickets are drafts the user may want to come back to —
      // skip the auto-dismiss-on-unmount so the unsent composer rehydrates
      // on next visit. Pricing widgets are different: a stale rate
      // suggestion shouldn't persist, so we keep dismissing those.
      if (info.actionType === 'open-support-ticket') continue;

      if (info.useNewPersistence && info.widgetId) {
        api.conversations.updateWidgetState(info.conversationId, info.widgetId, {
          messageId: info.messageId,
          actionType: info.actionType,
          status: 'dismissed',
        }).catch(() => {});
      } else {
        api.pricingConfigAgent.updateWidgetStatus(info.conversationId, info.messageId, info.partIndex ?? 0, 'dismissed', info.widgetId).catch(() => {});
      }
    }
  }, []);

  const onWidgetActive = useCallback((key: string, info: { conversationId: string; messageId: string; actionType: string; widgetId?: string; partIndex?: number; useNewPersistence?: boolean }) => {
    activeWidgetsRef.current.set(key, info);
  }, []);

  const onWidgetInactive = useCallback((key: string) => {
    activeWidgetsRef.current.delete(key);
  }, []);

  // Dismiss active widgets on unmount
  useEffect(() => () => { dismissActiveWidgets(); }, [dismissActiveWidgets]);

  // User role detection for marketing-only users
  const user = getUser();
  const isMarketingUser = user?.type === "MarketingOnly";
  const showAgentMode = isAllInOneUser();

  // A tutorial step can request a specific chat tab (e.g. open the pricing
  // agent on Agent mode). Accounts without Agent mode simply stay in Chat —
  // no error, no toggle shown.
  useEffect(() => {
    if (!tourChatModeRequest) return;
    if (tourChatModeRequest === "agent" && showAgentMode) {
      setChatMode("agent");
    } else if (tourChatModeRequest === "chat") {
      setChatMode("chat");
    }
    setTourChatModeRequest(null);
  }, [tourChatModeRequest, showAgentMode, setTourChatModeRequest]);

  const chatSampleQuestions = isMarketingUser
    ? ["How is my marketing performing?", "How is my website traffic?"]
    : [
        "How does my performance next week look?",
        "How is my marketing performing?",
        "Can you lower my rate floors for this weekend?",
      ];

  const agentSampleQuestions = [
    "Bump up my weekend rates — we're selling out too fast",
    "Add a 15% premium for the July 4th holiday weekend",
    "Price me 5% above my competitors for the next 90 days",
  ];

  const sampleQuestions = chatMode === "agent" ? agentSampleQuestions : chatSampleQuestions;

  const welcomeSubtext = chatMode === "agent"
    ? "Ask me about your pricing configuration, rate adjustments, or strategy & I will help optimize your revenue!"
    : isMarketingUser
      ? "Ask me about your marketing performance, website traffic, or something else & I will try to help!"
      : "Ask me about your hotel's performance, prices, or something else & I will try to help!";

  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;

  // Speech recognition
  const stt = useSpeechRecognition((text) => {
    setInput((prev) => (prev.trim() ? prev + " " + text : text));
  });

  // General chat hook
  const chatHook = useChatMessages({ conversationId: currentConversationId });

  // Pricing agent hook
  const agentHook = usePricingConfigAgentChat({ chatId: currentAgentChatId });

  // Select active hook based on mode
  const {
    messages,
    sendMessage,
    status,
    error,
  } = chatMode === "agent" ? agentHook : chatHook;

  const addToolApprovalResponse = chatMode === "chat" ? chatHook.addToolApprovalResponse : undefined;

  // Auto-resize textarea as user types
  useAutoResizeTextarea({
    textareaRef,
    value: input,
    minHeight: 32,
    maxHeight: 200,
  });

  // Smart scroll detection - only auto-scroll if user is at bottom
  const { isAtBottom, scrollToBottom } =
    useScrollDetection(messagesContainerRef);

  useEffect(() => {
    trackEvent("chat_opened");
  }, [trackEvent]);


  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Apply pending messages after hook re-initializes with new conversation ID
  useEffect(() => {
    if (pendingMessages && pendingMessages.length > 0) {
      chatHook.setMessages(pendingMessages as unknown as Parameters<typeof chatHook.setMessages>[0]);
      setPendingMessages(null);
    }
  }, [pendingMessages, chatHook.setMessages]);

  // Apply pending agent messages after agent hook re-initializes with new chat ID
  useEffect(() => {
    if (pendingAgentMessages && pendingAgentMessages.length > 0) {
      agentHook.setMessages(pendingAgentMessages as unknown as Parameters<typeof agentHook.setMessages>[0]);
      setPendingAgentMessages(null);
    }
  }, [pendingAgentMessages, agentHook.setMessages]);

  // Smart auto-scroll: scroll during streaming if user is at bottom
  useEffect(() => {
    // Auto-scroll if:
    // 1. User is at/near the bottom AND
    // 2. Either messages count changed OR content is streaming
    const isCurrentlyStreaming =
      status === "submitted" || status === "streaming";
    if (isAtBottom && (messages.length > 0 || isCurrentlyStreaming)) {
      scrollToBottom("smooth");
    }
  }, [messages, status, isAtBottom, scrollToBottom]);

  const handleSendMessage = () => {
    if (!input.trim() || (status !== "ready" && status !== "error")) return;

    if (!isOnline) {
      console.warn("[ChatV2Production] Cannot send message: offline");
      return;
    }

    setLastFailedMessage(input);
    sendMessage({ text: input });
    setInput("");

    // Restore focus to textarea after DOM updates
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    });
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSendMessage();
  };

  const handleRetry = () => {
    if (lastFailedMessage && isOnline) {
      setInput(lastFailedMessage);
      setLastFailedMessage("");
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    }
  };

  const handleApprove = useCallback(
    async (approvalId: string) => {
      if (!addToolApprovalResponse) return;
      try {
        await addToolApprovalResponse({
          id: approvalId,
          approved: true,
          reason: "User approved the action",
        });
      } catch (err) {
        console.error("[ChatV2Production] Failed to send approval:", err);
      }
    },
    [addToolApprovalResponse]
  );

  const handleDeny = useCallback(
    async (approvalId: string) => {
      if (!addToolApprovalResponse) return;
      try {
        await addToolApprovalResponse({
          id: approvalId,
          approved: false,
          reason: "User denied the action",
        });
      } catch (err) {
        console.error("[ChatV2Production] Failed to send denial:", err);
      }
    },
    [addToolApprovalResponse]
  );

  const handleConversationChange = useCallback(
    async (conversationId: string, mode: ChatMode = "chat") => {
      dismissActiveWidgets();
      setIsLoadingConversation(true);
      setChatMode(mode);

      try {
        const { api } = await import("../helpers/api");

        if (mode === "agent") {
          const result = await api.pricingConfigAgent.getMessages(conversationId);
          if (result.messages) {
            const aiSdkMessages = (result.messages as ServerMessage[]).map((msg) => ({
              id: msg.id,
              role: msg.role,
              content: msg.content,
              parts: msg.parts
                ? msg.parts.map((p: ServerMessagePart, pIdx: number) =>
                    p.type === "widget"
                      ? { type: "data-suggestion-action" as const, data: { ...p.widget, status: p.status, _messageId: msg.id, _partIndex: pIdx } }
                      : { type: "text" as const, text: p.text }
                  )
                : [{ type: "text" as const, text: msg.content }],
            }));
            setCurrentAgentChatId(conversationId);
            setIsExistingAgentConversation(true);
            setPendingAgentMessages(aiSdkMessages);
          }
        } else {
          const result = await api.conversations.getMessages(conversationId);
          if (result.messages) {
            const aiSdkMessages = (result.messages as ServerMessage[]).map((msg) => ({
              id: msg.id,
              role: msg.role,
              content: msg.content,
              // Convert server-saved parts back into AI SDK part shape.
              // Two formats coexist:
              //   1) Legacy `{ type: "widget", widget, widgetId, status }`
              //      — older conversations saved before the AI SDK v6 migration.
              //   2) Modern `{ type: "data-...", data: ... }` and `{ type: "text", text }`
              //      — what stream-parts-collector + ensureTextPart save today.
              // Without explicit handling for (2), all data-* parts (suggestions,
              // support tickets, custom reasoning, hotel metrics, …) silently
              // collapse to `{ type: "text", text: undefined }` and the widgets
              // never render on conversation reload.
              parts: msg.parts
                ? msg.parts.map((p: ServerMessagePart, pIdx: number) => {
                    if (p.type === "widget") {
                      return {
                        type: "data-suggestion-action" as const,
                        data: {
                          ...(p.widget ?? {}),
                          widgetId: p.widgetId ?? p.widget?.widgetId,
                          _messageId: msg.id,
                          _partIndex: pIdx,
                        },
                      };
                    }
                    if (typeof p.type === "string" && p.type.startsWith("data-")) {
                      const baseData =
                        p.data && typeof p.data === "object"
                          ? (p.data as Record<string, unknown>)
                          : {};
                      return {
                        type: p.type as MessagePart["type"],
                        data: {
                          ...baseData,
                          _messageId: msg.id,
                          _partIndex: pIdx,
                        },
                      };
                    }
                    if (p.type === "text") {
                      return { type: "text" as const, text: p.text ?? "" };
                    }
                    // Unknown legacy type — pass through verbatim rather than dropping.
                    return {
                      type: p.type as MessagePart["type"],
                      text: p.text,
                      data: p.data,
                    };
                  })
                : [{ type: "text" as const, text: msg.content }],
            }));
            setWidgetStates(result.widgetStates ?? {});
            setCurrentConversationId(conversationId);
            setIsExistingConversation(true);
            setPendingMessages(aiSdkMessages);
          }
        }
      } catch (error) {
        console.error("[ChatV2Production] Failed to load conversation:", error);
      } finally {
        setIsLoadingConversation(false);
      }

      setInput("");
    },
    [currentConversationId, agentHook, dismissActiveWidgets]
  );

  const handleNewChat = useCallback(() => {
    dismissActiveWidgets();
    if (chatMode === "agent") {
      const newAgentId = `chat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setCurrentAgentChatId(newAgentId);
      setIsExistingAgentConversation(false);
      agentHook.setMessages([]);
    } else {
      const newConversationId = crypto.randomUUID();
      setCurrentConversationId(newConversationId);
      setIsExistingConversation(false);
      setWidgetStates({});
      setPendingMessages(null);
      chatHook.setMessages([]);
    }
    setInput("");
  }, [chatMode, agentHook, chatHook, dismissActiveWidgets]);

  // Memoize processed messages to avoid re-running merge logic on every render
  const processedMessages = useMemo(
    () => mergeToolMessages(messages),
    [messages]
  );

  // Show loading widget when submitted OR streaming (covers entire request lifecycle)
  const isStreaming = status === "submitted" || status === "streaming";

  // Check if we should show standalone widget
  // Show when: user sent message AND (no assistant reply yet OR assistant reply has no visible content)
  const shouldShowStandaloneWidget = useMemo(() => {
    if (!isStreaming) return false;

    const lastMessage = processedMessages[processedMessages.length - 1];

    // If last message is user message, definitely show standalone widget
    if (!lastMessage || lastMessage.role === "user") return true;

    // If last message is assistant, check if it has visible content
    if (lastMessage.role === "assistant") {
      const hasVisibleContent = lastMessage.parts?.some((part) => {
        // Check for text content
        if (part.type === "text" && part.text?.trim()) return true;
        // Check for reasoning content
        if (part.type === "data-custom-reasoning") return true;
        // Check for tool events that would show in widget
        if (part.type === "tool-input-start" || part.type === "tool-call")
          return true;
        // Check for widget data
        if (
          part.type === "data-widget" ||
          part.type === "data-hotel-metrics-data"
        )
          return true;
        return false;
      });

      // Show standalone if no visible content yet
      return !hasVisibleContent;
    }

    return false;
  }, [processedMessages, isStreaming]);

  // Desktop layout. Two layouts share the panel envelope (rounded
  // corners + premium triple-shadow + isolation), but their internal
  // structure differs to match the Claude-Design mock:
  //
  //   compact (.a-panel.a-compact) — single column:
  //     panel > Header + (dividers + Notice + dividers + Body + Composer)
  //
  //   expanded (.a-panel.a-expanded) — rail + main column:
  //     panel > [Rail | Main { Header + Notice + Body + Composer }]
  //
  // Cream ambient wash covers the panel top in compact mode, and ONLY
  // the main column in expanded mode (so the rail's translucent grey
  // shows through). The expanded panel sits inside the dim+blur modal
  // overlay rendered by `pages/main.tsx`.
  if (isDesktop) {
    // `mainBody` = everything below the header: dividers + notice + the
    // scrollable message body + the composer. Reused identically in both
    // compact (sibling of Header) and expanded (sibling of Rail) layouts.
    const mainBody = (
      <div className="flex-1 flex flex-col min-w-0 min-h-0 w-full">
          {/* Notice + sandwich-dividers — only render while the
              disclaimer banner is visible. Once "Got it" is clicked the
              banner unmounts; previously the two hairline dividers
              remained and stacked as a doubled-up rule, so we gate them
              on the same visibility flag (broadcast via the
              `disclaimer-acknowledged` event from DisclaimerBanner).
              Expanded mode never renders the dividers (the notice
              free-floats), so we only branch on the visibility flag for
              compact. */}
          {disclaimerVisible && (
            <>
              <motion.div
                className="mx-[22px] flex-shrink-0"
                initial={false}
                animate={{
                  height: expanded ? 0 : 1,
                  opacity: expanded ? 0 : 1,
                }}
                transition={{ duration: 0 }}
                style={{ background: "var(--d-rule)" }}
              />
              {/* Banner wrapper — max-width caps at 720 in both modes so
                  switching expanded/compact never changes the constraint
                  (in compact the panel is only ~420px wide anyway, so the
                  720 cap doesn't actually constrain). Removes the snap
                  from the previous conditional className. */}
              <div className="w-full mx-auto" style={{ maxWidth: 720 }}>
                <DisclaimerBanner variant={expanded ? "expanded" : "compact"} />
              </div>
              <motion.div
                className="mx-[22px] flex-shrink-0"
                initial={false}
                animate={{
                  height: expanded ? 0 : 1,
                  opacity: expanded ? 0 : 1,
                }}
                transition={{ duration: 0 }}
                style={{ background: "var(--d-rule)" }}
              />
            </>
          )}

          {/* Content wrapper */}
          {/* Content wrapper — full width; horizontal centring of hero +
              composer is handled by their own max-w (720 / 8% padding). */}
          <div className="relative flex-1 min-h-0 w-full flex flex-col overflow-hidden">
          {/* `.a-body` (compact: padding 0 22px) / `.a-main-body`
              (expanded: padding 28px 8%). `justify-center` when the
              hero is showing so it sits visually centred between
              dividers and composer; messages flow top-down. */}
          <div
            ref={messagesContainerRef}
            className={`flex-1 min-h-0 w-full overflow-y-auto overflow-x-hidden flex-col items-stretch gap-3 flex relative ${
              processedMessages.length === 0 ? "justify-center" : "justify-start"
            }`}
            style={
              expanded
                ? { padding: "28px 8%" }
                : { padding: "16px 22px 0" }
            }
          >
          {isLoadingConversation && (
            <div className="flex items-center justify-center h-full w-full">
              <div className="text-center">
                <div
                  className="w-8 h-8 border-4 rounded-full animate-spin mx-auto mb-3"
                  style={{
                    borderColor: "var(--d-rule)",
                    borderTopColor: "var(--d-ink)",
                  }}
                />
                <p className="text-body text-content-secondary">
                  Loading conversation...
                </p>
              </div>
            </div>
          )}
          {!isLoadingConversation && processedMessages.length === 0 && (
            // Empty hero — ported from Claude-Design "Autumn Assistant".
            // Compact (`.a-hero`):  padding 18 0, gap 18, h 18 / sub 13 / sugg 13.5
            // Big    (`.a-hero[data-big="true"]`): padding 0, gap 28,
            //                                     h 28 / sub 15 / sugg 14.5
            // Horizontal padding lives on the body wrapper (22 compact,
            // 8% expanded). Hero adds only the vertical breathing room.
            <div
              data-big={expanded ? "true" : "false"}
              className="flex flex-col items-center justify-center h-full mx-auto w-full"
              style={{
                gap: expanded ? 28 : 18,
                maxWidth: expanded ? 720 : "none",
                paddingTop: expanded ? 0 : 18,
                paddingBottom: expanded ? 0 : 18,
                transition:
                  "gap 0ms, max-width 0ms, padding 0ms",
              }}
            >
              <div className="text-center">
                <h3
                  className="font-semibold mb-2"
                  style={{
                    fontFamily: "var(--d-font-sans)",
                    fontSize: expanded ? 28 : 18,
                    letterSpacing: expanded ? "-0.025em" : "-0.015em",
                    color: "var(--d-ink)",
                    transition:
                      "font-size 0ms, letter-spacing 0ms",
                  }}
                >
                  How can I help you today?
                </h3>
                <p
                  className="mx-auto"
                  style={{
                    fontFamily: "var(--d-font-body)",
                    fontSize: expanded ? 15 : 13,
                    color: "var(--d-ink-muted)",
                    maxWidth: expanded ? "56ch" : "38ch",
                    lineHeight: 1.55,
                    textWrap: "pretty",
                    transition:
                      "font-size 0ms, max-width 0ms",
                  }}
                >
                  {welcomeSubtext}
                </p>
              </div>
              <div
                className="flex flex-col gap-2 w-full mx-auto"
                style={{
                  maxWidth: expanded ? 560 : 384,
                  transition:
                    "max-width 0ms",
                }}
              >
                {sampleQuestions.map((prompt) => {
                  // `.a-sugg[data-active="true"]` — when the prompt text
                  // matches the composer's current value, pre-highlight
                  // (ink border + cream-soft bg). Honours the mock's
                  // "you picked this" affordance.
                  const isActive = input === prompt;
                  return (
                    <button
                      key={prompt}
                      onClick={() => {
                        setInput(prompt);
                        requestAnimationFrame(() => {
                          handleSendMessage();
                        });
                      }}
                      data-active={isActive ? "true" : "false"}
                      className="group flex items-center justify-between gap-3 text-left w-full"
                      style={{
                        padding: "13px 16px",
                        background: isActive
                          ? "var(--d-cream-soft)"
                          : "var(--d-surface)",
                        border: `1px solid ${
                          isActive ? "var(--d-ink)" : "var(--d-rule)"
                        }`,
                        borderRadius: 12,
                        boxShadow: "0 1px 2px rgba(28,26,22,0.02)",
                        fontFamily: "var(--d-font-sans)",
                        fontSize: expanded ? 14.5 : 13.5,
                        fontWeight: 500,
                        letterSpacing: "-0.005em",
                        color: "var(--d-ink)",
                        textWrap: "balance",
                        transition:
                          "font-size 0ms, background 200ms, border-color 200ms",
                      }}
                      onMouseEnter={(e) => {
                        if (isActive) return;
                        e.currentTarget.style.borderColor =
                          "var(--d-accent-soft)";
                        e.currentTarget.style.boxShadow =
                          "0 8px 24px -16px rgba(28,26,22,0.16)";
                        e.currentTarget.style.transform = "translateY(-1px)";
                      }}
                      onMouseLeave={(e) => {
                        if (isActive) return;
                        e.currentTarget.style.borderColor = "var(--d-rule)";
                        e.currentTarget.style.boxShadow =
                          "0 1px 2px rgba(28,26,22,0.02)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      <span className="min-w-0 flex-1">{prompt}</span>
                      <MoveRight
                        className="w-[14px] h-[14px] flex-shrink-0 -translate-x-1 opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100"
                        strokeWidth={ICON_STROKE}
                        style={{ color: "var(--d-accent-deep)" }}
                        aria-hidden="true"
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {!isLoadingConversation &&
            processedMessages.map((message, index) => {
              // Extract user message text from parts array (v6 format)
              const userMessageText =
                message.role === "user"
                  ? message.parts?.[0]?.text || message.content || ""
                  : "";

              // Detect if this is the last message and streaming
              const isLastMessage = index === processedMessages.length - 1;
              const isMessageStreaming =
                status === "streaming" && isLastMessage;

              // Hide intermediate assistant messages from the SAME streaming session
              // Only hide if the NEXT message is also an assistant (no user message between them)
              // This prevents duplicate reasoning widgets as AI SDK accumulates reasoning steps
              if (message.role === "assistant" && !isLastMessage) {
                const nextMessage = processedMessages[index + 1];

                // Only hide if next message is IMMEDIATELY an assistant message
                // If there's a user message between them, they're separate conversation turns
                if (nextMessage && nextMessage.role === "assistant") {
                  return null; // Skip this intermediate assistant message
                }
              }

              return message.role === "user" ? (
                <UserMessage key={message.id} message={userMessageText} />
              ) : message.role === "assistant" ? (
                <AssistantMessage
                  key={message.id}
                  message={message}
                  onApprove={handleApprove}
                  onDeny={handleDeny}
                  isStreaming={isMessageStreaming}
                  hideThinking={false}
                  compact={chatMode === "agent"}
                  expanded={expanded}
                  isExistingConversation={isExistingConversation || isExistingAgentConversation}
                  conversationId={chatMode === "agent" ? currentAgentChatId : currentConversationId}
                  widgetStates={chatMode === "chat" ? widgetStates : undefined}
                  onWidgetActive={onWidgetActive}
                  onWidgetInactive={onWidgetInactive}
                />
              ) : null;
            })}

          {/* Loading spinner while waiting for response */}
          {!isLoadingConversation && isStreaming && !shouldShowStandaloneWidget && (
            <div className="flex items-center gap-2 px-3 py-1">
              <div
                className="w-5 h-5 border-2 rounded-full animate-spin"
                style={{
                  borderColor: "var(--d-rule)",
                  borderTopColor: "var(--d-ink)",
                }}
              />
            </div>
          )}

          {/* Offline indicator */}
          {!isLoadingConversation && !isOnline && (
            <div className="w-full flex justify-center">
              <div className="text-center text-[12px] text-orange-600 p-2 bg-orange-50 rounded border border-orange-200 flex items-center justify-center gap-2">
                <span>You're offline. Check your connection.</span>
              </div>
            </div>
          )}

          {/* Error with retry */}
          {!isLoadingConversation && error && (
            <div className="w-full flex justify-center px-3">
              <ErrorDisplay
                error={error}
                onRetry={lastFailedMessage && isOnline ? handleRetry : undefined}
              />
            </div>
          )}

          {/* Show reasoning widget when streaming AND waiting for assistant response (chat mode only) */}
          {!isLoadingConversation && shouldShowStandaloneWidget && (
            <CustomReasoningWidget
              reasoningSteps={[]}
              toolEvents={[]}
              toolParts={[]}
              isStreaming={true}
            />
          )}

          <div ref={messagesEndRef} />

          {/* Scroll-to-bottom button — lives INSIDE the scroll
              container. `position: absolute` anchors it to the
              container's padding edge (NOT to the scrolled content),
              so it stays pinned to the bottom of the visible scroll
              area as messages flow. This keeps it visually attached
              to the chat scroll-viewport, just above (but separate
              from) the composer below. */}
          <ScrollToBottomButton
            containerRef={messagesContainerRef}
            endRef={messagesEndRef}
          />
        </div>

        {/* Composer wrapper — `.a-foot` (compact: 14/18/18) vs
            `.a-main-foot` (expanded: 16/8%/22). Inner `.a-composer` is
            capped at 720px and centred in expanded mode. flex-shrink-0
            so the composer never collapses under message pressure.
            Lives inside the content wrapper alongside the messages
            scroll-container and the floating scroll-to-bottom button,
            so the wrapper's `position: relative` is the anchor for the
            absolutely-positioned button. */}
        <div
          className="self-stretch relative flex-shrink-0"
          style={{
            // Both values in px so CSS can interpolate. Original used 8%
            // for expanded but mixing % with px breaks transition.
            paddingTop: expanded ? 16 : 14,
            paddingRight: expanded ? 96 : 18,
            paddingBottom: expanded ? 22 : 18,
            paddingLeft: expanded ? 96 : 18,
            transition: "padding 0ms",
          }}
        >
          <div
            className={`a-composer a-composer-aura transition-all mx-auto max-w-[720px] ${
              isComposerFocused ? "is-active" : ""
            } ${isComposerTyping ? "is-typing" : ""}`}
            data-has={input.trim().length > 0 ? "true" : "false"}
            data-big={expanded ? "true" : "false"}
            onFocusCapture={() => setIsComposerFocused(true)}
            onBlurCapture={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setIsComposerFocused(false);
              }
            }}
          >
          <div
            className="a-composer-aura-inner"
            style={{
              padding: expanded ? "16px 14px 10px" : "12px 12px 8px",
              transition: "padding 0ms",
            }}
          >
            <textarea
              ref={textareaRef}
              placeholder="Ask me anything…"
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                handleComposerType();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              rows={1}
              className="w-full border-0 ring-0 outline-none resize-none bg-transparent block"
              style={{
                fontFamily: "var(--d-font-body)",
                fontSize: expanded ? 15 : 14,
                lineHeight: 1.5,
                color: "var(--d-ink)",
                padding: "4px 4px 0",
                minHeight: expanded ? 30 : 24,
                maxHeight: expanded ? 200 : 140,
                transition:
                  "font-size 0ms, min-height 0ms",
              }}
            />

            {stt.interimText && (
              <div
                className="px-1 italic"
                style={{
                  fontFamily: "var(--d-font-body)",
                  fontSize: 12,
                  color: "var(--d-ink-ghost)",
                }}
              >
                {stt.interimText}
              </div>
            )}

            <div className="flex items-center justify-between pt-1">
              <button
                type="button"
                onClick={() => openSupportModal()}
                className="w-8 h-8 inline-flex items-center justify-center rounded-lg transition-colors"
                style={{ color: "var(--d-ink-muted)" }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "var(--d-accent-tint)";
                  e.currentTarget.style.color = "var(--d-ink)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = "var(--d-ink-muted)";
                }}
                aria-label="Help"
                title="Help"
                data-tutorial="chat-help"
              >
                <HelpCircle className="w-[16px] h-[16px]" strokeWidth={ICON_STROKE} />
              </button>

              <div className="flex items-center gap-1.5">
                {stt.isAvailable && (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={stt.toggle}
                      className="w-8 h-8 inline-flex items-center justify-center rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ color: "var(--d-ink-muted)" }}
                      onMouseEnter={(e) => {
                        if (e.currentTarget.disabled) return;
                        e.currentTarget.style.background = "var(--d-accent-tint)";
                        e.currentTarget.style.color = "var(--d-ink)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = "var(--d-ink-muted)";
                      }}
                      disabled={isStreaming || !isOnline || stt.isProcessing}
                      aria-label={stt.isListening ? "Stop listening" : "Start voice input"}
                      title={!isOnline ? "Voice input requires internet" : (stt.isListening ? "Stop listening" : "Start voice input")}
                    >
                      {stt.isListening ? (
                        <MicOff
                          className={`w-[16px] h-[16px] text-[var(--d-neg-text)] ${!prefersReducedMotion && 'animate-pulse'}`}
                          strokeWidth={ICON_STROKE}
                        />
                      ) : (
                        <Mic className="w-[16px] h-[16px]" strokeWidth={ICON_STROKE} />
                      )}
                    </button>

                    {stt.isListening && (
                      <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-footnote text-content-tertiary">
                        Listening...
                      </div>
                    )}

                    {stt.error && (
                      <div className="absolute -top-7 right-0 whitespace-nowrap text-[11px] leading-none px-2 py-1 rounded-md bg-[var(--d-neg-tint)] text-[var(--d-neg-text)] border border-[var(--d-neg-soft,var(--d-neg-text))]" style={{ borderRadius: "var(--d-r-md)" }}>
                        {stt.error}
                      </div>
                    )}

                    <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
                      {stt.isListening && "Listening for voice input"}
                      {stt.interimText && `Recognizing: ${stt.interimText}`}
                      {stt.error && stt.error}
                    </div>
                  </div>
                )}

                <ChatTalkButton />

                {/* Send — transparent ghost when empty, fills with ink when
                    there's text in the composer (mirrors the design's
                    `.a-send[data-active="true"]` rule). Lift on hover when
                    active. */}
                <button
                  type="button"
                  onClick={handleSendMessage}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-[10px] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  data-active={input.trim() ? "true" : "false"}
                  style={{
                    background: input.trim() ? "var(--d-ink)" : "var(--d-surface-2)",
                    color: input.trim() ? "#fff" : "var(--d-ink-muted)",
                    opacity: input.trim() ? undefined : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (input.trim()) {
                      e.currentTarget.style.background = "var(--d-accent-deep)";
                      e.currentTarget.style.transform = "translateY(-1px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (input.trim()) {
                      e.currentTarget.style.background = "var(--d-ink)";
                      e.currentTarget.style.transform = "translateY(0)";
                    }
                  }}
                  disabled={isStreaming || !input.trim()}
                  aria-label="Send message"
                >
                  {/* Send — lucide Send (paper plane), matches `Ic.Send`
                      in the mock. ICON_STROKE for kit-wide weight parity. */}
                  <Send className="w-[14px] h-[14px]" strokeWidth={ICON_STROKE} />
                </button>
              </div>
            </div>
          </div>
          </div>
        </div>
        <SupportTicketModal
          open={isSupportModalOpen}
          initialMessage={supportInitialMessage}
          onClose={() => setIsSupportModalOpen(false)}
          conversationContext={messages
            .filter((m) => m.role === "user" || m.role === "assistant")
            .map((m) => ({
              role: m.role,
              content:
                typeof m.content === "string"
                  ? m.content
                  : m.parts
                      ?.filter((p: { type: string }) => p.type === "text")
                      .map((p: { text?: string }) => p.text)
                      .join("") || "",
            }))
            .slice(-10)}
        />
        </div>
      </div>
    );

    // Shared Header instance used in both layouts.
    const headerNode = (
      <ProductionHeader
        onClose={onClose}
        onToggleExpanded={toggleExpanded}
        expanded={expanded}
        currentConversationId={
          chatMode === "agent"
            ? (isExistingAgentConversation ? currentAgentChatId : undefined)
            : (isExistingConversation ? currentConversationId : undefined)
        }
        onConversationChange={handleConversationChange}
        onNewChat={handleNewChat}
        chatMode={chatMode}
        onModeChange={showAgentMode ? setChatMode : undefined}
      />
    );

    return (
      <div className="hidden lg:block relative w-full h-full" data-tutorial="ai-copilot-chat">
      <div
        className="flex relative overflow-hidden w-full h-full flex-row"
        style={{
          background: "var(--d-surface)",
          borderRadius: expanded ? 18 : 16,
          // Inner ring is painted by the overlay below; the notch border is
          // the SVG SIBLING after this panel — it must live OUTSIDE the mask
          // (a child would be eaten by the cutout), see the note there.
          isolation: "isolate",
          // Right-edge notch cutout — the page bg shows THROUGH it. Narrow
          // (Sidebar-style) shape paired with the stroke-border sibling.
          ...getNotchMaskStyle({
            topPx: 55,
            transitionMs: 0,
            path: NARROW_NOTCH_PATH,
            viewW: NARROW_NOTCH_VIEW_W,
            viewH: NARROW_NOTCH_VIEW_H,
            width: CHAT_NOTCH_W,
            height: CHAT_NOTCH_H,
          }),
          transition:
            "border-radius 0ms, mask-position 0ms, -webkit-mask-position 0ms",
        }}
      >

        {/* Dusty-blue inner ring — painted ABOVE the content (z-10,
            pointer-events none) so the header/body backgrounds can't cover
            it the way an inset box-shadow on the panel itself would. Same
            colour as the notch stroke; the panel's notch mask cuts it along
            the cutout, and the SVG above redraws that edge. */}
        <div
          aria-hidden
          className="absolute inset-0 z-30 pointer-events-none"
          style={{
            borderRadius: expanded ? 18 : 16,
            boxShadow: "inset 0 0 0 1.5px #6f7e923b",
          }}
        />

        {/* Conversation rail — slides in from the left when the user
            expands the panel, slides out on collapse. Width animates
            from 0 → 280px so the main column reflows smoothly instead
            of snapping. The inner ConversationSidebar always keeps its
            natural 280px width (set inside the component), and the
            outer wrapper masks it via `overflow: hidden` while it
            grows/shrinks. */}
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              key="conv-rail"
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 280, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0 }}
              style={{
                overflow: "hidden",
                flexShrink: 0,
                height: "100%",
              }}
            >
              <ConversationSidebar
                currentConversationId={
                  chatMode === "agent"
                    ? (isExistingAgentConversation ? currentAgentChatId : undefined)
                    : (isExistingConversation ? currentConversationId : undefined)
                }
                onConversationChange={handleConversationChange}
                onNewChat={handleNewChat}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main column — ALWAYS rendered in the same DOM position so
            headerNode + mainBody (including ProductionHeader's chat /
            agent toggle, scroll position, focus state, etc.) never
            remount when expanded toggles. The cream wash is painted on
            this column in both modes: in compact it's effectively the
            whole panel; in expanded it sits to the right of the rail
            and the wash doesn't bleed under the rail's translucent
            grey. Previously this branch ternaried `<div> vs <>`, which
            forced React to unmount/remount the entire chat content
            chrome on every toggle — that was the "content disappears
            during collapse" bug. */}
        <div
          className="flex-1 flex flex-col min-w-0 min-h-0 relative"
          style={{
            background: "var(--d-surface)",
            backgroundImage:
              "radial-gradient(120% 100% at 10% 0%, rgba(241,235,229,0.9), rgba(241,235,229,0) 65%), radial-gradient(80% 100% at 100% 0%, rgba(214,220,228,0.45), rgba(214,220,228,0) 70%)",
          }}
        >
          {headerNode}
          {mainBody}
        </div>
      </div>

      {/* Refined stroke border for the notch — a SIBLING of the masked panel
          (NOT a child), so the panel's notch mask can't eat it. Drawn on top
          at the same coords/size as the cutout (right edge, top 55px,
          CHAT_NOTCH_W×H), same 29×51 shape, so the 1.5px dusty-blue stroke
          sits on the cut edge. */}
      <svg
        className="absolute pointer-events-none z-40"
        style={{ top: 55, right: 0 }}
        width={CHAT_NOTCH_W}
        height={CHAT_NOTCH_H}
        viewBox="0 0 29 51"
        preserveAspectRatio="none"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <g clipPath="url(#chat-notch-clip)">
          <mask
            id="chat-notch-mask"
            style={{ maskType: "luminance" }}
            maskUnits="userSpaceOnUse"
            x="0"
            y="0"
            width="29"
            height="51"
          >
            <path d="M29 0H0V51H29V0Z" fill="white" />
          </mask>
          <g mask="url(#chat-notch-mask)">
            <path
              d="M30.2422 0.75L29.7568 50.25H29.2275C29.111 48.1834 28.5275 45.2614 27.2773 42.4277C25.8697 39.2368 23.6053 36.1298 20.1875 34.5068C15.9065 32.4739 11.6965 31.4578 8.38184 30.668C6.72415 30.2729 5.29184 29.9344 4.18555 29.5547C3.63269 29.3648 3.16186 29.1657 2.78516 28.9434C2.41265 28.7234 2.11758 28.4731 1.9248 28.168C1.52212 27.5304 1.3284 26.8276 1.27051 26.1357L1.25391 25.8398V25.1602C1.27721 24.3748 1.46458 23.5607 1.9248 22.832C2.11758 22.5269 2.41265 22.2766 2.78516 22.0566C3.16186 21.8343 3.63269 21.6352 4.18555 21.4453C5.29184 21.0656 6.72415 20.7271 8.38184 20.332C11.6965 19.5422 15.9065 18.5261 20.1875 16.4932C23.6053 14.8702 25.8697 11.7632 27.2773 8.57227C28.5256 5.74258 29.1107 2.82138 29.2275 0.75H30.2422Z"
              stroke="#6F7E92"
              strokeOpacity="0.231373"
              strokeWidth="1.5"
            />
          </g>
        </g>
        <defs>
          <clipPath id="chat-notch-clip">
            <rect width="29" height="51" fill="white" />
          </clipPath>
        </defs>
      </svg>
      </div>
    );
  }

  // Mobile layout (fullscreen overlay) — same kit chrome as desktop:
  // cream ambient wash on top, soft elevation shadow, rounded corners.
  return (
    <div className="lg:hidden z-[999] fixed top-0 left-0 w-full h-full bg-[rgba(28,26,22,0.18)] backdrop-blur-[6px] flex justify-center items-start" data-tutorial="ai-copilot-chat">
      <div
        /* Safe-area sizing: `marginTop` + `height` (in the style below) use the
           env()-backed `--sat`/`--sab` tokens so the panel sits below the notch /
           Dynamic Island and above the home indicator on any device — replacing
           the old hardcoded 48px `mt-12`. marginTop + height == 100dvh, so the
           white surface reaches the bottom edge (the composer absorbs `--sab`).
           Paired with the overlay's `items-start` so the top offset is exact. */
        className="w-full flex-col justify-start items-start flex relative overflow-hidden"
        style={{
          // Safe-area handling: offset below the notch/Dynamic Island via the
          // env()-backed `--sat`; `height` now fills to the very bottom edge so
          // the white surface covers the home-indicator gutter (the composer
          // below adds `--sab` to its padding so the input still clears it).
          // Insets are 0 in a browser → a normal fullscreen panel there.
          marginTop: "calc(12px + var(--sat))",
          height: `calc(100dvh - 12px - var(--sat) - ${keyboardInset}px)`,
          background: "var(--d-surface)",
          backgroundImage:
            "radial-gradient(120% 100% at 10% 0%, rgba(241,235,229,0.9), rgba(241,235,229,0) 65%), radial-gradient(80% 100% at 100% 0%, rgba(214,220,228,0.45), rgba(214,220,228,0) 70%)",
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          isolation: "isolate",
        }}
      >
        {/* Dusty-blue inner ring above content (see desktop note). */}
        <div
          aria-hidden
          className="absolute inset-0 z-30 pointer-events-none"
          style={{
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            boxShadow: "inset 0 0 0 1.5px #6f7e923b",
          }}
        />
        <ProductionHeader
          onClose={onClose}
          currentConversationId={
            chatMode === "agent"
              ? (isExistingAgentConversation ? currentAgentChatId : undefined)
              : (isExistingConversation ? currentConversationId : undefined)
          }
          onConversationChange={handleConversationChange}
          onNewChat={handleNewChat}
          chatMode={chatMode}
          onModeChange={showAgentMode ? setChatMode : undefined}
        />

        {/* Disclaimer Banner */}
        <DisclaimerBanner />

        {/* Content wrapper */}
        <div className="relative flex-1 w-full flex flex-col overflow-hidden">
          {/* Messages */}
          <div className="flex-1 min-h-0 w-full overflow-y-auto overflow-x-hidden px-3 pt-3.5 pb-[5px] flex-col justify-start items-stretch gap-3 flex relative">
            {isLoadingConversation && (
              <div className="flex items-center justify-center h-full w-full">
                <div className="text-center">
                  <div
                  className="w-8 h-8 border-4 rounded-full animate-spin mx-auto mb-3"
                  style={{
                    borderColor: "var(--d-rule)",
                    borderTopColor: "var(--d-ink)",
                  }}
                />
                  <p className="text-body text-content-secondary">
                    Loading conversation...
                  </p>
                </div>
              </div>
            )}
            {!isLoadingConversation && processedMessages.length === 0 && (
              <div className="flex flex-col items-center justify-center h-full px-4 gap-[18px] py-4">
                <div className="text-center">
                  <h3
                    className="font-semibold mb-2"
                    style={{
                      fontFamily: "var(--d-font-sans)",
                      fontSize: 18,
                      letterSpacing: "-0.015em",
                      color: "var(--d-ink)",
                    }}
                  >
                    How can I help you today?
                  </h3>
                  <p
                    className="mx-auto"
                    style={{
                      fontFamily: "var(--d-font-body)",
                      fontSize: 13,
                      color: "var(--d-ink-muted)",
                      maxWidth: "38ch",
                      lineHeight: 1.55,
                      textWrap: "pretty",
                    }}
                  >
                    {welcomeSubtext}
                  </p>
                </div>
                <div className="flex flex-col gap-2 w-full max-w-sm mx-auto">
                  {sampleQuestions.map((prompt) => (
                    <button
                      key={prompt}
                      onClick={() => {
                        setInput(prompt);
                        requestAnimationFrame(() => {
                          handleSendMessage();
                        });
                      }}
                      className="group flex items-center justify-between gap-3 text-left w-full transition-all"
                      style={{
                        padding: "13px 16px",
                        background: "var(--d-surface)",
                        border: "1px solid var(--d-rule)",
                        borderRadius: 12,
                        boxShadow: "0 1px 2px rgba(28,26,22,0.02)",
                        fontFamily: "var(--d-font-sans)",
                        fontSize: 13.5,
                        fontWeight: 500,
                        letterSpacing: "-0.005em",
                        color: "var(--d-ink)",
                        textWrap: "balance",
                      }}
                    >
                      <span className="min-w-0 flex-1">{prompt}</span>
                      <MoveRight
                        className="w-[14px] h-[14px] flex-shrink-0 -translate-x-1 opacity-0 transition-all duration-200 group-hover:translate-x-0 group-hover:opacity-100"
                        strokeWidth={ICON_STROKE}
                        style={{ color: "var(--d-accent-deep)" }}
                        aria-hidden="true"
                      />
                    </button>
                  ))}
                </div>
              </div>
          )}
          {!isLoadingConversation &&
            processedMessages.map((message, index) => {
              // Extract user message text from parts array (v6 format)
              const userMessageText =
                message.role === "user"
                  ? message.parts?.[0]?.text || message.content || ""
                  : "";

              // Detect if this is the last message and streaming
              const isLastMessage = index === processedMessages.length - 1;
              const isMessageStreaming =
                status === "streaming" && isLastMessage;

              // Hide intermediate assistant messages from the SAME streaming session
              // Only hide if the NEXT message is also an assistant (no user message between them)
              // This prevents duplicate reasoning widgets as AI SDK accumulates reasoning steps
              if (message.role === "assistant" && !isLastMessage) {
                const nextMessage = processedMessages[index + 1];

                // Only hide if next message is IMMEDIATELY an assistant message
                // If there's a user message between them, they're separate conversation turns
                if (nextMessage && nextMessage.role === "assistant") {
                  return null; // Skip this intermediate assistant message
                }
              }

              return message.role === "user" ? (
                <UserMessage key={message.id} message={userMessageText} />
              ) : message.role === "assistant" ? (
                <AssistantMessage
                  key={message.id}
                  message={message}
                  onApprove={handleApprove}
                  onDeny={handleDeny}
                  isStreaming={isMessageStreaming}
                  hideThinking={false}
                  compact={chatMode === "agent"}
                  expanded={expanded}
                  isExistingConversation={isExistingConversation || isExistingAgentConversation}
                  conversationId={chatMode === "agent" ? currentAgentChatId : currentConversationId}
                  widgetStates={chatMode === "chat" ? widgetStates : undefined}
                  onWidgetActive={onWidgetActive}
                  onWidgetInactive={onWidgetInactive}
                />
              ) : null;
            })}

          {/* Loading spinner while waiting for response - mobile */}
          {!isLoadingConversation && isStreaming && !shouldShowStandaloneWidget && (
            <div className="flex items-center gap-2 px-3 py-1">
              <div
                className="w-5 h-5 border-2 rounded-full animate-spin"
                style={{
                  borderColor: "var(--d-rule)",
                  borderTopColor: "var(--d-ink)",
                }}
              />
            </div>
          )}

          {/* Error with retry - mobile */}
          {!isLoadingConversation && error && (
            <div className="w-full flex justify-center px-3">
              <ErrorDisplay
                error={error}
                onRetry={lastFailedMessage && isOnline ? handleRetry : undefined}
              />
            </div>
          )}

          {/* Show reasoning widget when streaming AND waiting for assistant response (chat mode only) */}
          {!isLoadingConversation && shouldShowStandaloneWidget && (
            <CustomReasoningWidget
              reasoningSteps={[]}
              toolEvents={[]}
              toolParts={[]}
              isStreaming={true}
            />
          )}

          <div ref={messagesEndRef} />

          {/* Scroll-to-bottom button — INSIDE the scroll container so
              its absolute position anchors to the scroll viewport's
              bottom edge, sitting just inside the messages area
              (above the composer below). */}
          <ScrollToBottomButton
            containerRef={messagesContainerRef}
            endRef={messagesEndRef}
          />
          </div>

        {/* Composer — same `.a-composer` chrome as desktop. Bottom padding adds
            the safe-area inset so the white panel fills below the input, down
            past the home indicator. */}
        <div className="self-stretch px-3 pt-2 pb-[calc(0.75rem+var(--sab))]">
          <div
            className={`a-composer-aura transition-all ${
              isComposerFocused ? "is-active" : ""
            } ${isComposerTyping ? "is-typing" : ""}`}
            onFocusCapture={() => setIsComposerFocused(true)}
            onBlurCapture={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                setIsComposerFocused(false);
              }
            }}
          >
          <div
            className="a-composer-aura-inner"
            style={{ padding: "12px 12px 8px" }}
          >
            <textarea
              ref={textareaRef}
              placeholder="Ask me anything…"
              onChange={(e) => {
                setInput(e.target.value);
                handleComposerType();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              value={input}
              rows={1}
              className="w-full border-0 ring-0 outline-none resize-none bg-transparent block"
              style={{
                fontFamily: "var(--d-font-body)",
                fontSize: 14,
                lineHeight: 1.5,
                color: "var(--d-ink)",
                padding: "4px 4px 0",
                minHeight: 24,
                maxHeight: 200,
              }}
            />

            {stt.interimText && (
              <div
                className="px-1 italic"
                style={{
                  fontFamily: "var(--d-font-body)",
                  fontSize: 12,
                  color: "var(--d-ink-ghost)",
                }}
              >
                {stt.interimText}
              </div>
            )}

            <div className="flex items-center justify-between pt-1">
              <button
                type="button"
                onClick={() => openSupportModal()}
                className="w-8 h-8 inline-flex items-center justify-center rounded-lg transition-colors"
                style={{ color: "var(--d-ink-muted)" }}
                aria-label="Contact support"
                title="Contact support"
                data-tutorial="chat-help"
              >
                <HelpCircle className="w-[16px] h-[16px]" strokeWidth={ICON_STROKE} />
              </button>

              <div className="flex items-center gap-1.5">
                {stt.isAvailable && (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={stt.toggle}
                      className="w-8 h-8 inline-flex items-center justify-center rounded-lg transition-colors disabled:opacity-50"
                      style={{ color: "var(--d-ink-muted)" }}
                      disabled={isStreaming || !isOnline || stt.isProcessing}
                      aria-label={stt.isListening ? "Stop listening" : "Start voice input"}
                    >
                      {stt.isListening ? (
                        <MicOff
                          className={`w-[16px] h-[16px] text-[var(--d-neg-text)] ${!prefersReducedMotion && 'animate-pulse'}`}
                          strokeWidth={ICON_STROKE}
                        />
                      ) : (
                        <Mic className="w-[16px] h-[16px]" strokeWidth={ICON_STROKE} />
                      )}
                    </button>

                    {stt.isListening && (
                      <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] text-content-tertiary">
                        Listening...
                      </div>
                    )}

                    {stt.error && (
                      <div className="absolute -top-7 right-0 whitespace-nowrap text-[11px] leading-none px-2 py-1 bg-[var(--d-neg-tint)] text-[var(--d-neg-text)] border border-[var(--d-neg-soft,var(--d-neg-text))]" style={{ borderRadius: "var(--d-r-md)" }}>
                        {stt.error}
                      </div>
                    )}

                    <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
                      {stt.isListening && "Listening for voice input"}
                      {stt.interimText && `Recognizing: ${stt.interimText}`}
                      {stt.error && stt.error}
                    </div>
                  </div>
                )}

                <ChatTalkButton />

                <button
                  type="button"
                  onClick={handleSendMessage}
                  className="w-8 h-8 inline-flex items-center justify-center rounded-[10px] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: input.trim() ? "var(--d-ink)" : "var(--d-surface-2)",
                    color: input.trim() ? "#fff" : "var(--d-ink-muted)",
                    opacity: input.trim() ? undefined : 1,
                  }}
                  disabled={isStreaming || !input.trim()}
                  aria-label="Send message"
                >
                  <Send className="w-[14px] h-[14px]" strokeWidth={ICON_STROKE} />
                </button>
              </div>
            </div>
          </div>
          </div>
        </div>
        <SupportTicketModal
          open={isSupportModalOpen}
          initialMessage={supportInitialMessage}
          onClose={() => setIsSupportModalOpen(false)}
          conversationContext={messages
            .filter((m) => m.role === "user" || m.role === "assistant")
            .map((m) => ({
              role: m.role,
              content:
                typeof m.content === "string"
                  ? m.content
                  : m.parts
                      ?.filter((p: { type: string }) => p.type === "text")
                      .map((p: { text?: string }) => p.text)
                      .join("") || "",
            }))
            .slice(-10)}
        />
        </div>
      </div>
    </div>
  );
}
